import { i18n } from 'i18n';
import { ProjectDto, RheologyDataDto } from 'providers/api';
import { selectableRheology } from 'utils/rheology';
import PCPipe from '../MineModel/Pipes/PCPipe';
import { PCStope } from '../MineModel/Stopes';
import { getUCSTargetDays } from '../canvas/PCFunctions/PCPasteFunctions';
import { RememberedInputs } from './rememberedInputs';
import { AutoPipeAssignerSettings, PipeUsage, StartFrom } from './types';

export interface SetupInputs {
  stopes: PCStope[];
  rheologyDataSetId?: string;
  pumpPressureBar?: number;
  // Blank runs at the dataset's default throughput.
  throughput?: number;
  // Optional: the target only sets the binder, which moves pressures by about 1 %, so blank runs at the minimum binder.
  targetUCS?: number;
  curingDays?: number;
  allowancePercent?: number;
  excludedPipeIds: string[];
  usage: Record<string, PipeUsage>;
  startFrom: StartFrom;
}

export type SetupField = 'stopes' | 'rheology' | 'pumpPressure' | 'throughput' | 'allowance' | 'usage';

export interface SetupValidation {
  errors: Partial<Record<SetupField, string>>;
  warnings: string[];
  settings?: AutoPipeAssignerSettings;
}

export const hasUcsData = (rheology: RheologyDataDto | undefined): boolean => Object.keys(rheology?.ucsCoefficients ?? {}).length > 0;

export const curingDayOptions = (rheology: RheologyDataDto | undefined): number[] => Object.keys(rheology?.ucsCoefficients ?? {})
  .map(Number)
  .sort((a, b) => a - b);

const defaultCuringDays = (rheology: RheologyDataDto | undefined, preferred?: number): number | undefined => {
  if (!rheology || !hasUcsData(rheology)) return undefined;
  return preferred !== undefined && curingDayOptions(rheology).includes(preferred) ? preferred : getUCSTargetDays(rheology.ucsCoefficients);
};

export const datasetThroughput = (rheology: RheologyDataDto | undefined): number | undefined => (
  rheology ? rheology.tonnage.default || rheology.tonnage.min : undefined
);

export const effectiveThroughput = (inputs: SetupInputs, rheology: RheologyDataDto | undefined) => inputs.throughput ?? datasetThroughput(rheology);

// A newly chosen dataset brings its own pump pressure, throughput and curing ages.
export const withRheology = (inputs: SetupInputs, rheology: RheologyDataDto): SetupInputs => ({
  ...inputs,
  rheologyDataSetId: rheology.rheologyDataSetId,
  pumpPressureBar: rheology.pumpPressure.default,
  throughput: undefined,
  curingDays: defaultCuringDays(rheology, inputs.curingDays),
});

export const initialSetupInputs = (
  project: ProjectDto,
  rheologies: RheologyDataDto[],
  pipes: PCPipe[],
  remembered: RememberedInputs,
  stopes: PCStope[],
): SetupInputs => {
  const selectable = selectableRheology(rheologies);
  const rheology = selectable.find(({ rheologyDataSetId }) => rheologyDataSetId === remembered.rheologyDataSetId) ?? selectable[0];
  const sameDataset = !!rheology && rheology.rheologyDataSetId === remembered.rheologyDataSetId;
  return {
    stopes,
    rheologyDataSetId: rheology?.rheologyDataSetId,
    pumpPressureBar: sameDataset ? remembered.pumpPressureBar : rheology?.pumpPressure.default,
    throughput: sameDataset ? remembered.throughput : undefined,
    targetUCS: remembered.targetUCS,
    curingDays: defaultCuringDays(rheology, remembered.curingDays),
    allowancePercent: remembered.allowancePercent ?? project.defaultTransientPercentage ?? 0,
    excludedPipeIds: remembered.excludedPipeIds ?? ((project.defaultExcludedPipeIds ?? []) as string[]),
    usage: pipes.reduce<Record<string, PipeUsage>>((acc, { pipeId }) => ({ ...acc, [pipeId]: remembered.usage[pipeId] ?? 'level' }), {}),
    startFrom: remembered.startFrom ?? 'current',
  };
};

const isPositive = (value: number | undefined): value is number => value !== undefined && Number.isFinite(value) && value > 0;

export const validateSetup = (inputs: SetupInputs, rheology: RheologyDataDto | undefined): SetupValidation => {
  const usesUcs = hasUcsData(rheology);
  const throughput = effectiveThroughput(inputs, rheology);
  const usages = Object.values(inputs.usage);

  const allowance = inputs.allowancePercent ?? 0;

  const errors: SetupValidation['errors'] = {
    stopes: inputs.stopes.length === 0 ? i18n._('Select at least one stope.') : undefined,
    rheology: rheology ? undefined : i18n._('Choose a rheology dataset.'),
    // The dataset's limits only describe its design point, so the solver runs at any positive pump pressure or throughput.
    pumpPressure: isPositive(inputs.pumpPressureBar) ? undefined : i18n._('Enter a pump pressure above zero.'),
    throughput: isPositive(throughput) ? undefined : i18n._('Enter a throughput above zero.'),
    allowance: Number.isFinite(allowance) && allowance >= 0 && allowance <= 100 ? undefined : i18n._('Enter a percentage from 0 to 100.'),
    usage: usages.includes('level') ? undefined : i18n._('Mark at least one pipe as Level.'),
  };
  const warnings = usages.includes('borehole') ? [] : [i18n._('No pipe is marked Borehole, so an overpressured borehole cannot be upgraded.')];
  const valid = Object.values(errors).every((error) => !error);

  return {
    errors,
    warnings,
    settings: valid && rheology ? {
      rheologyDataSetId: rheology.rheologyDataSetId,
      pumpPressureBar: inputs.pumpPressureBar as number,
      throughput: throughput as number,
      // A target of 0 makes the correlation's water/binder ratio unbounded, so every Cw clamps to the dataset's minimum binder.
      targetUCS: usesUcs && isPositive(inputs.targetUCS) ? inputs.targetUCS : 0,
      curingDays: usesUcs ? inputs.curingDays ?? getUCSTargetDays(rheology.ucsCoefficients) : 0,
      allowancePercent: allowance,
      excludedPipeIds: inputs.excludedPipeIds,
      startFrom: inputs.startFrom,
    } : undefined,
  };
};
